import {
 useEffect, useRef, useCallback, useMemo, useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Api } from '@common/services/api';
import { enqueueOneSignalOperation } from '@common/utils/one-signal-operation-queue';
import {
  bindActiveConversationStorageSync,
  syncActiveConversationFromRoute,
} from '@common/utils/active-conversation-tracker';
import {
  dismissBrowserNotificationsForConversation,
  extractPushNotificationDataFromEvent,
  getActiveConversationIdFromLocation,
  getConversationIdFromPushPayload,
  isViewingChatConversationRoute,
  shouldSuppressChatPushNotification,
} from '@common/utils/push-notification-chat';
import selectNetwork from '../../modules/network/actions/select-network';
import selectOrganisation from '../../modules/organisation/actions/select-organisation';
import { ESourceTypes } from '../definitions';
import type { StoreState } from '../types/store';

// Types of notifications that should trigger navigation
const NOTIFICATION_TYPE_WHITELIST = new Set([
  ESourceTypes.CONVERSATION,
  ESourceTypes.MESSAGE,
  ESourceTypes.EXCHANGE,
  ESourceTypes.EVENT,
  ESourceTypes.ACCESS_REQUEST,
  ESourceTypes.PERFORMANCE_EVALUATION,
  ESourceTypes.SURVEY,
  ESourceTypes.RECOGNITION_MESSAGE,
  ESourceTypes.RECEIVED_RECOGNITION,
  ESourceTypes.DOCUMENT,
  ESourceTypes.CHANNEL,
  ESourceTypes.RECOGNITION,
  ESourceTypes.FORM_SUBMISSION,
  ESourceTypes.CHANNEL_ACCESS_REQUEST,
  ESourceTypes.REMIND_TO_APPROVE_EXCHANGE,
  ESourceTypes.CHANNEL_ACCESS_REQUEST_APPROVED,
  ESourceTypes.COURSE,
  ESourceTypes.SCHEDULE,
  ESourceTypes.NOTE,
  ESourceTypes.USER,
]);

interface PushNotificationData {
  type?: string;
  id?: string;
  source_type?: string;
  source_id?: string;
  activity_type?: string;
  /** Form id for form submission notifications (submission id is usually `id`). */
  formId?: string;
  form_id?: string;
  evaluation_id?: string;
  network_id?: string | null;
  organisation_id?: string;
  week_start_date?: string;
  track_name?: string;
  channel_id?: string;
  meta?: {
    channel?: {
      id?: string;
    };
    form?: {
      id?: string;
    };
    document?: {
      page?: unknown;
      is_folder?: boolean;
      attachment?: unknown;
      parent_folder_id?: string;
    };
  };
}

/** Normalise `organisation.selected` (full organisation in prod; some tests use a raw id string). */
function selectedOrganisationId(
  selected: StoreState['organisation']['selected'] | null | undefined
): string | undefined {
  if (selected == null) return undefined;
  if (typeof selected === 'string') return selected;
  return selected.id;
}

function getPushNotificationType(data: PushNotificationData): string | undefined {
  return data.type || data.source_type;
}

function isHighFiveRecognitionMessage(trackName: string | undefined): boolean {
  return trackName === ESourceTypes.RECEIVED_RECOGNITION ||
    trackName === 'highfive_added' ||
    trackName === 'highfive_added_for_mention';
}

/**
 * Pure route builder for push deep-links. Pass **current** `networkSelected` from Redux
 * after any `selectOrganisation` / `selectNetwork` awaits — do not close over hook state.
 */
function buildPushNotificationRoute(
  data: PushNotificationData,
  isAdmin: boolean,
  networkSelectedFallback: string | null | undefined,
  originEncoded: string
): string | null {
  const {
    id,
    source_id,
    network_id,
    week_start_date,
  } = data;
  const type = getPushNotificationType(data);
  const sourceId = source_id || id;
  const activityId = source_id ? id : undefined;
  const trackName = data.track_name || data.activity_type;
  const formId = data.formId || data.form_id || data.meta?.form?.id;
  const document = data.meta?.document;
  const networkId = network_id || networkSelectedFallback;
  const evaluationId =
    type === ESourceTypes.PERFORMANCE_EVALUATION
      ? sourceId || data.evaluation_id
      : sourceId;

  const channelId =
    type === ESourceTypes.CHANNEL_ACCESS_REQUEST ||
    type === ESourceTypes.CHANNEL_ACCESS_REQUEST_APPROVED ||
    type === ESourceTypes.CHANNEL
      ? data.channel_id || data.meta?.channel?.id || sourceId
      : null;

  switch (type) {
    case ESourceTypes.CONVERSATION:
      return isAdmin
        ? `/admin/conversations/${sourceId}`
        : `/conversations/${sourceId}`;
    case ESourceTypes.MESSAGE:
      return activityId
        ? `/messages/${sourceId}?aid=${activityId}`
        : `/messages/${sourceId}`;
    case ESourceTypes.RECOGNITION_MESSAGE:
      if (!sourceId) return null;
      if (isHighFiveRecognitionMessage(trackName)) {
        return isAdmin
          ? `/admin/highfives/${sourceId}`
          : `/highfives/${sourceId}`;
      }
      return activityId
        ? `/messages/${sourceId}?aid=${activityId}`
        : `/messages/${sourceId}`;
    case ESourceTypes.EXCHANGE:
      if (!sourceId) return null;
      return `/calendar/exchanges/${sourceId}`;
    case ESourceTypes.EVENT:
      return sourceId ? `/events/${sourceId}` : null;
    case ESourceTypes.ACCESS_REQUEST:
      return network_id
        ? '/users/filter/requests'
        : '/admin/users/filter/requests';
    case ESourceTypes.CHANNEL:
      return networkId && channelId
        ? `/networks/${networkId}/messages/channel/${channelId}`
        : null;
    case ESourceTypes.PERFORMANCE_EVALUATION:
      if (!evaluationId) return null;
      if (isAdmin) {
        return `/admin/performance/evaluations/${evaluationId}`;
      }
      return `/performance/my-evaluations/${evaluationId}`;
    case ESourceTypes.RECOGNITION:
    case ESourceTypes.RECEIVED_RECOGNITION:
      if (!sourceId) return null;
      return isAdmin ? `/admin/highfives/${sourceId}` : `/highfives/${sourceId}`;
    case ESourceTypes.DOCUMENT:
      if (!sourceId) return null;
      if (document?.page) {
        return isAdmin
          ? `/admin/documents/page/${sourceId}`
          : `/documents/page/${sourceId}`;
      }
      if (document?.is_folder) {
        return isAdmin ? `/admin/documents/${sourceId}` : `/documents/${sourceId}`;
      }
      if (document?.attachment) {
        const folderPath = document.parent_folder_id
          ? `/documents/${document.parent_folder_id}`
          : '/documents';
        return `${isAdmin ? '/admin' : ''}${folderPath}?aid=${sourceId}`;
      }
      if (isAdmin) {
        return `/admin/documents/${sourceId}`;
      }
      return `/documents/${sourceId}`;
    case ESourceTypes.SURVEY:
      if (!sourceId) return null;
      return `/surveys/${sourceId}/1?origin=${originEncoded}`;
    case ESourceTypes.CHANNEL_ACCESS_REQUEST:
      if (!channelId) return null;
      if (!networkId) return null;
      return `/networks/${networkId}/messages/channel/${channelId}`;
    case ESourceTypes.CHANNEL_ACCESS_REQUEST_APPROVED:
      if (!channelId) return null;
      if (!networkId) return null;
      return `/networks/${networkId}/messages/channel/${channelId}`;
    case ESourceTypes.REMIND_TO_APPROVE_EXCHANGE:
      if (!sourceId) return null;
      return `/calendar/exchanges/${sourceId}`;
    case ESourceTypes.COURSE:
      if (!sourceId) return null;
      return `/academy/courses/${sourceId}`;
    case ESourceTypes.SCHEDULE:
      if (!sourceId) return null;
      if (!week_start_date) return null;
      return `/calendar/${week_start_date}/schedule/${sourceId}`;
    case ESourceTypes.NOTE:
      return sourceId ? `/profile/notes?noteId=${sourceId}` : '/profile/notes';
    case ESourceTypes.USER:
      return sourceId ? `/users/${sourceId}/about` : null;
    case ESourceTypes.FORM_SUBMISSION: {
      if (!sourceId) return null;
      const submissionId = sourceId;
      const formIdForRoute = formId;
      const formsBase = isAdmin ? '/admin/forms' : '/forms';
      if (trackName === 'form_submission_created') {
        if (!formIdForRoute) return null;
        return `/forms/${formIdForRoute}/edit/submissions/${submissionId}`;
      }
      if (trackName === 'form_submission_status_updated') {
        if (!formIdForRoute) return null;
        return `/forms/${formIdForRoute}/submission/${submissionId}`;
      }
      if (trackName === 'form_submission_comment_created') {
        return null;
      }
      if (trackName === 'form_submission_note_created') {
        if (!formIdForRoute) return null;
        return `${formsBase}/${formIdForRoute}/edit/submissions/${submissionId}?form=moderate&showComments=true`;
      }
      return null;
    }
    default:
      return null;
  }
}

function networkIdsDiffer(
  payloadId: string | null | undefined,
  selectedId: string | null | undefined
): boolean {
  return String(payloadId ?? '') !== String(selectedId ?? '');
}

function getFormIdFromSubmissionResponse(result: any): string | null {
  const submission = result?.data;
  const formId =
    submission?.form?.id ||
    submission?.form_id ||
    submission?.formId ||
    submission?.form?.uuid;

  return typeof formId === 'string' && formId ? formId : null;
}

async function tryResolveFormIdFromSubmissionUrl(url: string): Promise<string | null> {
  try {
    const result = await Api.get(url);
    return getFormIdFromSubmissionResponse(result);
  } catch {
    return null;
  }
}

function collectFormIdsFromFormsList(items: any[]): string[] {
  return items.flatMap((item) => {
    const children = Array.isArray(item.children)
      ? collectFormIdsFromFormsList(item.children)
      : [];
    const formId = item.type === 'form'
      ? item.id || item.data?.id
      : item.data?.type === 'form'
        ? item.data?.id
        : null;

    return typeof formId === 'string' && formId
      ? [formId, ...children]
      : children;
  });
}

async function resolveModeratorFormIdFromSubmission(
  orgId: string,
  submissionId: string
): Promise<string | null> {
  try {
    const result = await Api.get(
      `/v2/organisations/${orgId}/forms/moderators/me?limit=100`
    );
    const items = Array.isArray(result?.data) ? result.data : [];
    const formIds = collectFormIdsFromFormsList(items);
    const checks = await Promise.all(
      formIds.map(async (formId) => {
        try {
          await Api.get(`/v1/organisations/${orgId}/forms/${formId}/submissions/${submissionId}`);
          return formId;
        } catch {
          return null;
        }
      })
    );

    return checks.find((formId): formId is string => !!formId) || null;
  } catch {
    return null;
  }
}

async function resolveFormIdFromSubmission(
  orgId: string,
  submissionId: string
): Promise<string | null> {
  const ownSubmissionUrl =
    `/v1/organisations/${orgId}/users/me/forms/submissions/${submissionId}`;
  const organisationSubmissionUrl =
    `/v1/organisations/${orgId}/forms/submissions/${submissionId}`;

  return (
    await tryResolveFormIdFromSubmissionUrl(ownSubmissionUrl) ||
    await tryResolveFormIdFromSubmissionUrl(organisationSubmissionUrl) ||
    await resolveModeratorFormIdFromSubmission(orgId, submissionId)
  );
}

/**
 * Submitter: own submission GET succeeds → .../submission/.../comments
 * Moderator: own GET fails, moderated submissions GET succeeds → .../edit/submissions/...?form=moderate&showComments=true
 * Otherwise null (mirrors network/template.jsx FORM_SUBMISSION comment handling.)
 */
async function resolveFormSubmissionCommentPath(
  orgId: string,
  formId: string | null,
  submissionId: string,
  formsBase: string
): Promise<string | null> {
  const ownSubmissionsUrl = `/v1/organisations/${orgId}/users/me/forms/submissions/${submissionId}`;

  try {
    const result = await Api.get(ownSubmissionsUrl);
    const resolvedFormId = formId || getFormIdFromSubmissionResponse(result);
    if (resolvedFormId) {
      return `${formsBase}/${resolvedFormId}/submission/${submissionId}/comments`;
    }
  } catch {
    // Fall through to moderator path.
  }

  const resolvedFormId = formId || await resolveFormIdFromSubmission(orgId, submissionId);
  if (!resolvedFormId) return null;

  const moderatedSubmissionsUrl = `/v1/organisations/${orgId}/forms/${resolvedFormId}/submissions/${submissionId}`;
  try {
    await Api.get(moderatedSubmissionsUrl);
    return `${formsBase}/${resolvedFormId}/edit/submissions/${submissionId}?form=moderate&showComments=true`;
  } catch {
    return null;
  }
}

async function resolveFormSubmissionNotePath(
  orgId: string,
  formId: string | null,
  submissionId: string,
  formsBase: string
): Promise<string | null> {
  const resolvedFormId = formId || await resolveFormIdFromSubmission(orgId, submissionId);
  if (!resolvedFormId) return null;

  return `${formsBase}/${resolvedFormId}/edit/submissions/${submissionId}?form=moderate&showComments=true`;
}

type OneSignalSdk = typeof import('react-onesignal').default;

declare global {
  interface Window {
    __ONESIGNAL_APP_ID__?: string;
  }
}

/** Node SSR sets `global.window = {}` without `location`; do not treat that as a browser. */
function hasBrowserWindow(): boolean {
  if (typeof window === 'undefined' || process.env.SERVER) return false;
  return typeof window.location?.hostname === 'string';
}

function getRuntimeOneSignalAppId(): string | undefined {
  if (!hasBrowserWindow()) return undefined;
  const appId = window.__ONESIGNAL_APP_ID__;
  if (!appId || typeof appId !== 'string') return undefined;
  return appId;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function parseJsonRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'string') return undefined;

  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function getAppIdFromOneSignalConfig(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;

  const appId = value.app_id || value.appId;
  return typeof appId === 'string' && appId ? appId : undefined;
}

/** Hostnames declared on whitelabel `urls.*` — any match means we may use org OneSignal keys on this deployment. */
function getWhitelabelUrlHostnames(config: Record<string, unknown>): string[] {
  if (!isRecord(config.urls)) return [];

  const hosts: string[] = [];
  for (const value of Object.values(config.urls)) {
    if (typeof value !== 'string' || !value) continue;
    try {
      hosts.push(new URL(value).hostname);
    } catch {
      // ignore malformed entries
    }
  }
  return hosts;
}

function isCurrentWhiteLabelWebUrl(config: Record<string, unknown>): boolean {
  if (!hasBrowserWindow()) return false;

  const hosts = getWhitelabelUrlHostnames(config);
  if (!hosts.length) return false;

  return hosts.includes(window.location.hostname);
}

function getOneSignalAppIdFromWhiteLabelConfig(
  config: Record<string, unknown>
): string | undefined {
  const oneSignalConfig = config.onesignal;
  if (Array.isArray(oneSignalConfig)) {
    return oneSignalConfig
      .map(getAppIdFromOneSignalConfig)
      .find((appId): appId is string => !!appId);
  }

  return getAppIdFromOneSignalConfig(oneSignalConfig);
}

function getOrganisationOneSignalAppId(
  selected: StoreState['organisation']['selected'] | null | undefined
): string | undefined {
  if (!isRecord(selected)) return undefined;

  const config = isRecord(selected.whitelabel_config)
    ? selected.whitelabel_config
    : parseJsonRecord(selected.whitelabel_config);

  if (!config || !isCurrentWhiteLabelWebUrl(config)) return undefined;

  return getOneSignalAppIdFromWhiteLabelConfig(config);
}

function resolvePushAppId(
  selected: StoreState['organisation']['selected'] | null | undefined
): string | undefined {
  const fromOrg = getOrganisationOneSignalAppId(selected);
  const fromWindow = getRuntimeOneSignalAppId();
  const fromEnv = process.env.ONESIGNAL_APP_ID;
  return (
    (typeof fromOrg === 'string' && fromOrg) ||
    (typeof fromWindow === 'string' && fromWindow) ||
    (typeof fromEnv === 'string' && fromEnv) ||
    undefined
  );
}

const PUSH_INIT_KEY_SEP = '\u001e';

/** One-time per user: re-bind OneSignal session after SW/cache resets (stuck subscribers). */
const ONESIGNAL_PUSH_MIGRATION_STORAGE_KEY = 'onesignal_push_session_v1';

function pushInitStorageKey(userId: string, appId: string): string {
  return `${userId}${PUSH_INIT_KEY_SEP}${appId}`;
}

function pushMigrationStorageKey(userId: string, appId: string): string {
  return `${ONESIGNAL_PUSH_MIGRATION_STORAGE_KEY}${PUSH_INIT_KEY_SEP}${pushInitStorageKey(userId, appId)}`;
}

async function runStuckPushSubscriberMigration(
  OneSignal: OneSignalSdk,
  userId: string,
  appId: string
): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const migrationKey = pushMigrationStorageKey(userId, appId);
  if (localStorage.getItem(migrationKey) === userId) {
    return;
  }

  try {
    const hasPermission = OneSignal.Notifications.permission;
    if (hasPermission) {
      try {
        await OneSignal.logout();
      } catch {
        // User may not have an active session.
      }
      await OneSignal.login(userId);
      const subscription = OneSignal.User.PushSubscription;
      if (!subscription.optedIn) {
        await subscription.optIn();
      }
    }
    localStorage.setItem(migrationKey, userId);
  } catch (migrationError) {
    console.error('OneSignal push migration error:', migrationError);
  }
}

function isOneSignalAlreadyInitializedError(error: unknown): boolean {
  if (typeof error === 'string') return error.includes('already initialized');
  if (!isRecord(error)) return false;

  const { message } = error;
  return typeof message === 'string' && message.includes('already initialized');
}

/**
 * Hook to manage push notifications using react-onesignal.
 * Should be called in CoreTemplate when user is logged in and app is initialized.
 */
export const usePushNotifications = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore();

  /** Raw store id — reliable after org init; avoid `selected` selector which treats `{}` as truthy. */
  const rawUserId = useSelector(
    (state: StoreState) => state.loggedUser?.user?.id as string | number | undefined
  );
  const userId =
    rawUserId != null && rawUserId !== '' ? String(rawUserId) : undefined;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const pushAppIdRef = useRef<string | undefined>(undefined);

  const organisationSelected = useSelector(
    (state: StoreState) => state.organisation?.selected
  );
  const isOrganisationInitialised = useSelector(
    (state: StoreState) => state.app?.organisationInitialised
  );

  /**
   * Stable across organisation Redux merges — only changes when org id or whitelabel-derived
   * OneSignal app id changes. Using raw `organisationSelected` in the effect deps re-ran the
   * effect on every merge, set `cancelled` on the in-flight boot, and aborted `login` before
   * OneSignal called `/users` / `/identity` (looked fine only after full refresh).
   */
  const pushOrgFingerprint = useMemo(() => {
    const oid = selectedOrganisationId(organisationSelected) ?? '';
    const wlApp = getOrganisationOneSignalAppId(organisationSelected) ?? '';
    return `${oid}|${wlApp}`;
  }, [organisationSelected]);

  /** Set only after login + subscription / listeners succeeded for `userId` + `pushAppId`. */
  const lastSuccessfulPushInitKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const clickHandlerRef = useRef<((event: any) => void) | null>(null);
  const foregroundWillDisplayHandlerRef =
    useRef<((event: any) => void) | null>(null);
  const permissionChangeHandlerRef =
    useRef<((hasPermission: boolean) => void) | null>(null);
  const handleNotificationClickRef =
    useRef<((data: PushNotificationData) => void) | null>(null);
  const oneSignalRef = useRef<OneSignalSdk | null>(null);
  const [isOneSignalReady, setIsOneSignalReady] = useState(false);

  const handleNotificationClick = useCallback(
    async (data: PushNotificationData) => {
      const notificationType = getPushNotificationType(data);
      if (
        !isMountedRef.current ||
        !notificationType ||
        !NOTIFICATION_TYPE_WHITELIST.has(notificationType)
      ) {
        return;
      }

      const isAdmin = window.location.pathname.includes('/admin');
      const getState = () => store.getState() as StoreState;

      let snap = getState();
      const needsOrganisationSelection =
        !!data.organisation_id &&
        (!selectedOrganisationId(snap.organisation?.selected) ||
          data.organisation_id !==
            selectedOrganisationId(snap.organisation?.selected));

      try {
        if (needsOrganisationSelection && data.organisation_id) {
          await (dispatch as any)(
            selectOrganisation(data.organisation_id, false)
          );
        }

        snap = getState();
        const currentNetworkId = snap.network?.selected;
        const payloadNetworkId = data.network_id;
        const needsNetworkSelection =
          payloadNetworkId != null &&
          payloadNetworkId !== '' &&
          networkIdsDiffer(payloadNetworkId, currentNetworkId);

        if (needsNetworkSelection) {
          await (dispatch as any)(
            selectNetwork(String(payloadNetworkId), false, true)
          );
        }
      } catch (error) {
        console.error('Error selecting network/organisation:', error);
        return;
      }

      snap = getState();
      const freshNetworkId = snap.network?.selected ?? undefined;
      const pathOrigin = encodeURIComponent(
        `${history.location.pathname}${history.location.search}`
      );

      const orgId = data.organisation_id;

      if (
        notificationType === ESourceTypes.FORM_SUBMISSION &&
        (
          (data.track_name || data.activity_type) === 'form_submission_comment_created' ||
          (data.track_name || data.activity_type) === 'form_submission_note_created'
        )
      ) {
        const formIdForRoute = data.formId || data.form_id || data.meta?.form?.id || null;
        const submissionId = data.source_id || data.id;
        if (!orgId || !submissionId) return;

        const formsBase = isAdmin ? '/admin/forms' : '/forms';
        const path =
          (data.track_name || data.activity_type) === 'form_submission_comment_created'
            ? await resolveFormSubmissionCommentPath(
              orgId,
              formIdForRoute,
              submissionId,
              formsBase
            )
            : await resolveFormSubmissionNotePath(
              orgId,
              formIdForRoute,
              submissionId,
              formsBase
            );
        if (path) history.push(path);
        return;
      }

      const targetRoute = buildPushNotificationRoute(
        data,
        isAdmin,
        freshNetworkId,
        pathOrigin
      );

      if (!targetRoute) return;

      history.push(targetRoute);
    },
    [dispatch, history, store]
  );

  handleNotificationClickRef.current = handleNotificationClick;

  useEffect(() => bindActiveConversationStorageSync(), []);

  useEffect(() => {
    if (!hasBrowserWindow()) return;

    const syncRoute = () => {
      syncActiveConversationFromRoute(
        history.location.pathname,
        history.location.search
      );
    };

    syncRoute();
    const unlisten = history.listen(syncRoute);
    return unlisten;
  }, [history]);

  useEffect(() => {
    if (!hasBrowserWindow()) return;

    const dismissVisibleChatNotifications = () => {
      const conversationId =
        getActiveConversationIdFromLocation(
          window.location.pathname,
          window.location.search
        );
      if (conversationId) {
        void dismissBrowserNotificationsForConversation(conversationId);
      }
    };

    if (!isViewingChatConversationRoute(
      window.location.pathname,
      window.location.search
    )) {
      return;
    }

    dismissVisibleChatNotifications();
    const intervalId = window.setInterval(dismissVisibleChatNotifications, 1500);
    return () => window.clearInterval(intervalId);
  }, [history.location.pathname, history.location.search]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const message = event.data;
      if (!isRecord(message)) return;
      if (message.type !== 'ONESIGNAL_NOTIFICATION_CLICK') return;

      const { additionalData, targetUrl } = message;
      if (isRecord(additionalData) && handleNotificationClickRef.current) {
        handleNotificationClickRef.current(additionalData as PushNotificationData);
        return;
      }

      if (typeof targetUrl === 'string') {
        try {
          const url = new URL(targetUrl);
          history.push(`${url.pathname}${url.search}${url.hash}`);
        } catch {
          history.push(targetUrl);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [history]);

  const organisationSelectedRef = useRef(organisationSelected);
  organisationSelectedRef.current = organisationSelected;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId || !isOrganisationInitialised || !isOneSignalReady) {
      return;
    }

    const removePushEventListeners = () => {
      const OneSignal = oneSignalRef.current;
      if (OneSignal) {
        if (clickHandlerRef.current) {
          try {
            OneSignal.Notifications.removeEventListener(
              'click' as any,
              clickHandlerRef.current
            );
          } catch {
            // Ignore cleanup errors
          }
        }
        if (foregroundWillDisplayHandlerRef.current) {
          try {
            OneSignal.Notifications.removeEventListener(
              'foregroundWillDisplay' as any,
              foregroundWillDisplayHandlerRef.current
            );
          } catch {
            // Ignore cleanup errors
          }
        }
        if (permissionChangeHandlerRef.current) {
          try {
            OneSignal.Notifications.removeEventListener(
              'permissionChange' as any,
              permissionChangeHandlerRef.current
            );
          } catch {
            // Ignore cleanup errors
          }
        }
      }
      clickHandlerRef.current = null;
      foregroundWillDisplayHandlerRef.current = null;
      permissionChangeHandlerRef.current = null;
    };

    const attachPushEventListeners = () => {
      const OneSignal = oneSignalRef.current;
      if (!OneSignal?.Notifications) return;

      removePushEventListeners();

      const clickHandler = (event: any) => {
        const data = extractPushNotificationDataFromEvent(event);
        if (data && handleNotificationClickRef.current) {
          handleNotificationClickRef.current(data as PushNotificationData);
        }
      };

      const foregroundWillDisplayHandler = (event: any) => {
        const data = extractPushNotificationDataFromEvent(event);
        if (
          shouldSuppressChatPushNotification(
            data,
            window.location.pathname,
            window.location.search
          )
        ) {
          try {
            event.preventDefault();
          } catch {
            // Older browsers / SDK builds may not support preventDefault.
          }
          const conversationId = getConversationIdFromPushPayload(data);
          if (conversationId) {
            void dismissBrowserNotificationsForConversation(conversationId);
          }
        }
      };

      const permissionChangeHandler = (hasPermission: boolean) => {
        if (hasPermission && isMountedRef.current) {
          OneSignal.User.PushSubscription.optIn().catch(() => {});
        }
      };

      clickHandlerRef.current = clickHandler;
      foregroundWillDisplayHandlerRef.current = foregroundWillDisplayHandler;
      permissionChangeHandlerRef.current = permissionChangeHandler;

      OneSignal.Notifications.addEventListener('click', clickHandler);
      OneSignal.Notifications.addEventListener(
        'foregroundWillDisplay',
        foregroundWillDisplayHandler
      );
      OneSignal.Notifications.addEventListener(
        'permissionChange',
        permissionChangeHandler
      );
    };

    attachPushEventListeners();
    const reattachOnResume = () => attachPushEventListeners();
    window.addEventListener('focus', reattachOnResume);
    document.addEventListener('visibilitychange', reattachOnResume);
    const reattachInterval = window.setInterval(attachPushEventListeners, 10000);

    return () => {
      window.removeEventListener('focus', reattachOnResume);
      document.removeEventListener('visibilitychange', reattachOnResume);
      window.clearInterval(reattachInterval);
      removePushEventListeners();
    };
  }, [userId, isOrganisationInitialised, isOneSignalReady]);

  useEffect(() => {
    if (!userId || !isOrganisationInitialised) {
      setIsOneSignalReady(false);
      return;
    }

    if (!hasBrowserWindow()) return;

    /** Effect instance user — cleanup runs after the next render; `userIdRef` may already be the next user. */
    const effectUserId = userId;

    let cancelled = false;

    const runPushEffectCleanup = () => {
      setIsOneSignalReady(false);

      const latestUserId = userIdRef.current;
      const latestAppId = pushAppIdRef.current;
      const expectedKey =
        latestUserId && latestAppId
          ? pushInitStorageKey(latestUserId, latestAppId)
          : null;

      if (!latestUserId) {
        lastSuccessfulPushInitKeyRef.current = null;
      }

      const shouldLogout =
        !latestUserId ||
        (lastSuccessfulPushInitKeyRef.current &&
          expectedKey &&
          lastSuccessfulPushInitKeyRef.current !== expectedKey);

      if (shouldLogout) {
        void enqueueOneSignalOperation(async () => {
          try {
            const OneSignal = oneSignalRef.current;
            if (OneSignal?.User) await OneSignal.logout();
          } catch (error) {
            // Ignore logout errors during cleanup
          }
        });
      }
    };

    const delay = (ms: number) => new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

    const bootOneSignal = async () => {
      const staleKey = lastSuccessfulPushInitKeyRef.current;
      if (staleKey) {
        const staleUser = staleKey.split(PUSH_INIT_KEY_SEP)[0];
        if (staleUser !== userId) {
          lastSuccessfulPushInitKeyRef.current = null;
        }
      }

      let appId = resolvePushAppId(organisationSelectedRef.current);
      /* OneSignal app id can appear on `window` after first paint (SSR inline script). */
      /* eslint-disable no-await-in-loop */
      for (let i = 0; i < 20 && !appId && !cancelled; i += 1) {
        await delay(100);
        if (cancelled) return;
        appId = resolvePushAppId(organisationSelectedRef.current);
      }
      /* eslint-enable no-await-in-loop */

      if (!appId || cancelled) return;

      pushAppIdRef.current = appId;
      const initKey = pushInitStorageKey(userId, appId);

      if (lastSuccessfulPushInitKeyRef.current === initKey) {
        isMountedRef.current = true;
        const OneSignal = oneSignalRef.current;
        if (OneSignal?.User) {
          try {
            await OneSignal.login(userId);
          } catch (loginErr) {
            console.error('OneSignal re-login (fast path) error:', loginErr);
            lastSuccessfulPushInitKeyRef.current = null;
          }
        }
        if (!cancelled && pushInitStorageKey(userId, appId) === initKey) {
          setIsOneSignalReady(true);
        }
        return;
      }

      const previousKey = lastSuccessfulPushInitKeyRef.current;
      const previousUserId = previousKey
        ? previousKey.split(PUSH_INIT_KEY_SEP)[0]
        : null;

      isMountedRef.current = true;

      try {
        if (!isMountedRef.current || cancelled) return;

        if (!oneSignalRef.current) {
          const mod = await import('react-onesignal');
          if (!isMountedRef.current || cancelled) return;
          if (pushInitStorageKey(userId, appId) !== initKey) return;
          oneSignalRef.current = mod.default;
        }
        const OneSignal = oneSignalRef.current;
        if (!OneSignal) return;

        if (previousUserId && previousUserId !== userId) {
          try {
            if (OneSignal.User) {
              await OneSignal.logout();
            }
          } catch (logoutError: any) {
            console.error(
              'Error logging out previous user from OneSignal:',
              logoutError
            );
          }
        }

        if (!isMountedRef.current || cancelled) return;
        if (pushInitStorageKey(userId, appId) !== initKey) return;

        try {
          await OneSignal.init({
            appId,
            notificationClickHandlerMatch: 'origin',
            notificationClickHandlerAction: 'focus',
            allowLocalhostAsSecureOrigin: true,
          });
        } catch (initError: any) {
          if (!isOneSignalAlreadyInitializedError(initError)) {
            throw initError;
          }
        }

        if (!isMountedRef.current || cancelled) return;

        try {
          if (OneSignal.User) {
            await OneSignal.logout();
          }
        } catch {
          // Ignore logout errors (user might not be logged in)
        }

        await OneSignal.login(userId);
        if (!isMountedRef.current || cancelled) return;
        if (pushInitStorageKey(userId, appId) !== initKey) return;

        await runStuckPushSubscriberMigration(OneSignal, userId, appId);

        if (OneSignal.Notifications.isPushSupported()) {
          const hasPermission = OneSignal.Notifications.permission;
          if (!hasPermission) {
            const permissionGranted =
              await OneSignal.Notifications.requestPermission();
            if (permissionGranted && isMountedRef.current && !cancelled) {
              await OneSignal.User.PushSubscription.optIn();
            }
          } else {
            const subscription = OneSignal.User.PushSubscription;
            if (!subscription.optedIn && isMountedRef.current && !cancelled) {
              await subscription.optIn();
            }
          }
        }

        if (!isMountedRef.current || cancelled) return;
        if (pushInitStorageKey(userId, appId) !== initKey) return;
        lastSuccessfulPushInitKeyRef.current = initKey;
        setIsOneSignalReady(true);
      } catch (error) {
        console.error('OneSignal initialization error:', error);
        setIsOneSignalReady(false);
      }
    };

    void enqueueOneSignalOperation(async () => {
      if (cancelled) return;
      await bootOneSignal();
    });

    return () => {
      cancelled = true;
      if (userIdRef.current !== effectUserId) {
        const stale = lastSuccessfulPushInitKeyRef.current;
        if (
          stale &&
          stale.startsWith(`${effectUserId}${PUSH_INIT_KEY_SEP}`)
        ) {
          lastSuccessfulPushInitKeyRef.current = null;
        }
      }
      runPushEffectCleanup();
    };
  }, [userId, isOrganisationInitialised, pushOrgFingerprint]);

  return null;
};

export default usePushNotifications;
